"use client";

import { colors, Fade, NoSsr, Skeleton, useTheme } from "@mui/material";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Grid from "@mui/material/Grid2";
import Typography from "@mui/material/Typography";
import CenterBox from "@v2/components/styled/CenterBox";
import FlexBox from "@v2/components/styled/FlexBox";
import TextOverflow from "@v2/components/styled/TextOverflow";
import { numberFormat } from "@v2/helpers/optimizedHelpers";
import { uiColors } from "@v2/lib/color";
import { ChevronLeft } from "lucide-react";
import Image from "next/image";
import Link from "next/link";
import { Fragment, useCallback, useState } from "react";
import Countdown from "react-countdown";
import { Navigation, Pagination } from "swiper/modules";
import { Swiper, SwiperSlide } from "swiper/react";

export interface ProductDiscount {
    id: number;
    title: string;
    min_apply_price: number | null;
    type: string;
    value: number;
    max_value: number;
    available_at: string;
    expire_at: string;
    link: string | null;
    site_products: PDSiteProduct[];
}

export interface PDSiteProduct {
    id: number;
    slug: string;
    brand_id: number;
    title: string;
    is_triable: number;
    priority_type: string;
    priority: number;
    has_quantity: number;
    products: PDProduct[];
}

export interface PDProduct {
    id: number;
    price: string;
    rial_price: number;
    discount: number;
    discount_expire_at: string;
    final_price: number;
    currency_id: number;
    barcode: string;
    quantity: number;
    color_code: string;
    name: string;
    images: PDProductImage[];
}

export interface PDProductImage {
    id: string;
    extension: string;
    link: string;
    default: boolean;
    title: string | null;
    alt: string | null;
}


interface IProps {
    productDiscount?: ProductDiscount
}

export default function ProductDiscount({ productDiscount }: IProps) {
    const theme = useTheme()
    const [isEnded, setIsEnded] = useState(false)
    const onEndDiscount = useCallback(() => {
        setIsEnded(true)
    }, [])
    if (isEnded || !productDiscount || !productDiscount?.site_products?.length) return <></>
    return (
        <Box sx={{
            mb: { xs: 5, md: 3 },
            px: { md: 3, lg2: 0 },
        }}>
            <Grid
                container
                sx={{
                    background: '#c1323e',
                    borderRadius: { xs: 0, md: 1 },

                    [theme.breakpoints.up('sm')]: {
                        overflow: "hidden",
                        // height: 416,
                        alignItems: 'stretch',
                    }
                }}
            >
                <Grid size={{ xs: 12, sm: 4, md: 3.5, lg: 2.7 }} sx={{
                    position: 'relative',
                    boxShadow: "rgba(0, 0, 0, 0.07) 0px 1px 2px, rgba(0, 0, 0, 0.07) 0px 2px 4px, rgba(0, 0, 0, 0.07) 0px 4px 8px, rgba(0, 0, 0, 0.07) 0px 8px 16px, rgba(0, 0, 0, 0.07) 0px 16px 32px, rgba(0, 0, 0, 0.07) 0px 32px 64px",
                    zIndex: 10,
                }} >
                    <FlexBox sx={{ alignItems: 'center', height: '100%' }} >
                        <Grid container flex={1} py={{ xs: 4, sm: 0 }}>
                            <Grid size={{ xs: 12, md: 12 }} order={{ xs: 1, md: 1 }}>
                                <Typography
                                    component="h2"
                                    sx={{
                                        fontSize: { xs: 20, sm: 22, md: 24, lg: 30 },
                                        fontWeight: 700,
                                        color: '#fff',
                                        textAlign: "center",
                                    }}
                                >
                                    {productDiscount?.title}
                                </Typography>
                                <Typography sx={{
                                    color: '#fff',
                                    textAlign: 'center'
                                }}>تا {numberFormat(productDiscount.value)} {productDiscount.type == "percent" ? "درصد" : "ریال"} تخفیف</Typography>
                            </Grid>
                            <Grid size={{ xs: 12, md: 12 }} order={{ xs: 2, md: 2 }}>
                                <CenterBox sx={{
                                    my: {
                                        xs: 2,
                                        sm: 6
                                    }
                                }}>
                                    <NoSsr fallback={<Box height={70} />}>
                                        <Countdown onComplete={onEndDiscount} autoStart date={productDiscount.expire_at} renderer={countDownRenderer} />
                                    </NoSsr>
                                </CenterBox>
                            </Grid>
                            <Grid size={{ xs: 12, md: 12 }} order={{ xs: 3, md: 3 }}>
                                <FlexBox justifyContent={'center'}>
                                    <Button
                                        endIcon={
                                            <ChevronLeft
                                                color={"#fff"}
                                                size={13}
                                            />
                                        }
                                        variant="contained"
                                        disableElevation
                                        component={Link}
                                        prefetch={false}
                                        href={productDiscount.link ?? `/fa/products?pd=${productDiscount.id}`}
                                        sx={{
                                            backgroundColor: undefined,
                                            color: "#fff",
                                            width: { xs: 135, md: 150 },
                                            height: 42,
                                            fontSize: { xs: 14, md: 16 },
                                        }}
                                    >
                                        مشاهده همه
                                    </Button>
                                </FlexBox>
                            </Grid>
                        </Grid>
                    </FlexBox>
                </Grid>

                <Grid size={{ xs: 12, sm: 8, md: 8.5, lg: 9.3 }}>
                    <NoSsr fallback={<LoadingSwiper />}>
                        <Box
                            sx={{
                                py: 2,
                                display: "flex",
                                alignItems: "center",
                                height: "100%",
                                userSelect: 'none',
                                position: 'relative',
                                overflow: 'hidden',
                                '&::after': {
                                    content: '""',
                                    position: 'absolute',
                                    top: 0,
                                    right: -1,
                                    bottom: 0,
                                    width: '1px',
                                    zIndex: 10,
                                    boxShadow: "rgba(0, 0, 0, 0.15) 0px 1px 2px, rgba(0, 0, 0, 0.15) 0px 2px 4px, rgba(0, 0, 0, 0.1) 0px 4px 8px, rgba(0, 0, 0, 0.1) 0px 8px 16px, rgba(0, 0, 0, 0.1) 0px 16px 32px, rgba(0, 0, 0, 0.07) 0px 32px 64px",

                                },
                                "& .swiper-button-prev.swiper-button-disabled": {
                                    transform: 'translateX(-200px)',
                                    transitionDelay: '300ms',
                                },
                                "& .swiper-button-next.swiper-button-disabled": {
                                    transform: 'translateX(200px)',
                                    transitionDelay: '300ms',
                                },
                                "& .swiper-button-prev, & .swiper-button-next": {
                                    transitionDelay: '0',
                                    transition: '400ms',
                                    pointerEvents: 'unset !important',
                                    marginRight: 1, marginLeft: 1,
                                    bgcolor: '#fff',
                                    height: 48, width: 48,
                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                    borderRadius: '50%',
                                    '&::after': {
                                        fontSize: 24, color: colors.grey[700]
                                    }
                                },
                                '& .swiper-pagination': {
                                    '& .swiper-pagination-bullet': {
                                        borderRadius: 3,
                                        transition: '180ms !important'
                                    },
                                    '& .swiper-pagination-bullet-active': {
                                        background: colors.grey[800],
                                    }
                                },
                                // px: 2,
                                // mx: 2
                                '& .swiper-wrapper': {
                                    overflow: "visible",
                                    // width: "auto",
                                    // mr: 2,
                                    // ml: 2,
                                },
                                '& .swiper': {
                                    // the swiper is a flex item, so without a width it shrinks
                                    // to its slides and collapses when the campaign has few products
                                    width: '100%',
                                    minWidth: 0,
                                    pr: 2,
                                    pl: 2,
                                }
                            }}
                        >
                            <Swiper
                                // slidesOffsetAfter={4}
                                // slidesOffsetBefore={4}
                                // slidesPerGroup={1}
                                slidesPerView={3.5}
                                spaceBetween={8}
                                // slidesOffsetAfter={16}
                                // slidesOffsetBefore={16}
                                modules={[Navigation, Pagination]}
                                // freeMode
                                pagination={{
                                    enabled: true,
                                    dynamicBullets: true,
                                    dynamicMainBullets: 2,
                                }}
                                navigation
                                // grabCursor
                                // centeredSlides
                                breakpoints={{
                                    [theme.breakpoints.values.lg2]: {
                                        slidesPerView: 3.5
                                    },
                                    [theme.breakpoints.values.lg]: {
                                        slidesPerView: 3.2
                                    },
                                    [theme.breakpoints.values.md]: {
                                        slidesPerView: 2.5
                                    },
                                    [theme.breakpoints.values.sm]: {
                                        slidesPerView: 1.8
                                    },
                                    [theme.breakpoints.values.xs]: {
                                        slidesPerView: 1.5
                                    },

                                }}
                            >
                                {productDiscount?.site_products?.map((site_product, index) => {

                                    const isFirst = index === 0;
                                    const isLast = index === productDiscount?.site_products.length - 1
                                    const splitTitle = site_product.title.split('-')
                                    const firstTitle = splitTitle.shift()?.trim();
                                    const secondTitle = splitTitle.join().trim();
                                    const showProduct = site_product.products[0]
                                    let discountPercent = (showProduct.discount / showProduct.rial_price) * 100;
                                    if (!showProduct.rial_price) {
                                        discountPercent = 15
                                    }
                                    return (
                                        <SwiperSlide key={site_product.id} style={{
                                            // marginRight: isFirst ? '16px' : 0,
                                            // marginLeft: isLast ? '16px' : 0,
                                        }}>
                                            <Fade in={true} style={{
                                                transitionDelay: `${(50 * index)}ms`
                                            }}>
                                                <Box
                                                    component={Link}
                                                    href={'/fa/products/' + site_product.slug}
                                                    prefetch={false}
                                                    target="_blank"
                                                    sx={{
                                                        display: 'block',
                                                        background: "#fff",
                                                        borderTopLeftRadius: isFirst ? 8 : 0,
                                                        borderBottomLeftRadius: isFirst ? 8 : 0,
                                                        borderTopRightRadius: isLast ? 8 : 0,
                                                        borderBottomRightRadius: isLast ? 8 : 0,
                                                        overflow: "hidden",
                                                        transition: '350ms',
                                                        '& .collection_image': {
                                                            transition: '500ms',
                                                        },
                                                        '&:hover': {

                                                            bgcolor: colors.grey[50],

                                                            '& .collection_image': {
                                                                // transform: 'scale(1.1)'
                                                            }
                                                        }
                                                    }}
                                                >
                                                    <Box className="collection_image_wrapper" sx={{
                                                        width: '100%',
                                                        aspectRatio: '1 / 1',
                                                        overflow: 'hidden',
                                                        position: 'relative'
                                                    }}>
                                                        {!site_product.has_quantity && (
                                                            <Box sx={{
                                                                position: 'absolute',

                                                                right: -44,
                                                                top: 18,
                                                                width: 150,
                                                                bgcolor: "#c1323e",
                                                                transform: 'rotate(-42deg)'
                                                            }}>
                                                                <Typography sx={{
                                                                    color: '#fff',
                                                                    textAlign: 'center',
                                                                    fontSize: 14
                                                                }}>تمام شد !</Typography>
                                                            </Box>
                                                        )}
                                                        <Box className="collection_image" sx={{
                                                            backgroundImage: `url(${process.env.NEXT_PUBLIC_MEDIA_URL}/api/v1/media/${site_product.products[0].images[0].id}?height=400&width=520&action=cropThumbnail)`,
                                                            backgroundSize: 'contain',
                                                            backgroundPosition: 'center',
                                                            // height: 280,
                                                            width: '100%',
                                                            height: '100%',
                                                        }} />
                                                    </Box>

                                                    <Box sx={{ p: 2, pt: 0 }}>
                                                        <TextOverflow sx={{
                                                            fontSize: 15,
                                                            fontWeight: 700,
                                                            color: "#000000DE",
                                                            mt: 0.7
                                                        }}>
                                                            {firstTitle}
                                                        </TextOverflow>
                                                        <TextOverflow sx={{ direction: 'rtl', textAlign: 'right', color: colors.grey[500], fontSize: 13, mt: 0.3 }}>
                                                            {secondTitle}
                                                        </TextOverflow>

                                                        <Box sx={{ display: "flex", alignItems: "center", justifyContent: 'space-between', mt: 1 }}>
                                                            <Box sx={{ display: "flex", alignItems: "center", height: 27 }}>

                                                                <Typography
                                                                    sx={{
                                                                        fontSize: 17,
                                                                        fontWeight: 700,
                                                                        color: "#333",
                                                                        mr: 0.5,
                                                                    }}
                                                                >
                                                                    {site_product.products[0].rial_price !== 0 && (
                                                                        numberFormat(site_product.products[0].final_price / 10)
                                                                    )}
                                                                </Typography>


                                                                <Typography
                                                                    sx={{
                                                                        fontSize: 13,
                                                                        fontWeight: 400,
                                                                        color: "#00000099",
                                                                    }}
                                                                >
                                                                    {site_product.products[0].rial_price !== 0 ? "تومانءء" : "نـامــوجـود"}
                                                                </Typography>
                                                            </Box>
                                                            <Box>
                                                                <Typography component={'span'} className="en-num" sx={{
                                                                    color: uiColors.primary,
                                                                    fontSize: 13,
                                                                    mr: '4px',
                                                                    [theme.breakpoints.down('sm')]: {
                                                                        fontSize: 11
                                                                    },
                                                                }}>
                                                                    {!site_product.has_quantity && "با "} تخفیف
                                                                </Typography>
                                                                <Typography component={'span'} sx={{
                                                                    bgcolor: uiColors.primary,
                                                                    borderRadius: '4px',
                                                                    color: '#fff',
                                                                    fontSize: 13,
                                                                    py: '2px',
                                                                    px: '5px',
                                                                    [theme.breakpoints.down('sm')]: {
                                                                        fontSize: 11
                                                                    },
                                                                }}>
                                                                    %{discountPercent.toFixed(0)}
                                                                </Typography>

                                                            </Box>
                                                        </Box>
                                                        <Box sx={{
                                                            display: 'flex',
                                                            alignItems: 'center',
                                                            justifyContent: 'space-between'
                                                        }}>
                                                            <Typography
                                                                sx={{
                                                                    fontSize: 16,
                                                                    fontWeight: 500,
                                                                    color: colors.grey[700],
                                                                    mr: 0.5,
                                                                    textDecoration: 'line-through'
                                                                }}
                                                            >
                                                                {site_product.products[0].rial_price !== 0 && (
                                                                    numberFormat(site_product.products[0].rial_price / 10)
                                                                )}
                                                            </Typography>
                                                            {!site_product.has_quantity && (
                                                                <Typography sx={{
                                                                    fontSize: 17,
                                                                    fontWeight: 600,
                                                                    color: colors.red[800]
                                                                }}>فروخته شد !</Typography>
                                                            )}
                                                        </Box>

                                                    </Box>
                                                </Box>
                                            </Fade>
                                        </SwiperSlide>
                                    )
                                })}
                            </Swiper>
                        </Box>
                    </NoSsr>

                </Grid>
            </Grid>
        </Box>
    );
}

const calc = (swiperNum: number) => (12 / swiperNum)

const LoadingSwiper = () => (
    <Grid container spacing={1} p={2} pr={2} sx={{}} >
        <Grid size={{ xs: calc(1.5), sm: calc(1.8), md: calc(2.5), lg: calc(3.2), lg2: calc(3.5) }} sx={{
            display: { xs: 'block' }
        }}> <SwiperItemSkeleton first /> </Grid>
        <Grid size={{ xs: calc(1.5), sm: calc(1.8), md: calc(2.5), lg: calc(3.2), lg2: calc(3.5) }} sx={{
            display: { xs: 'none', md: 'block' }
        }}> <SwiperItemSkeleton /> </Grid>
        <Grid size={{ xs: calc(1.5), sm: calc(1.8), md: calc(2.5), lg: calc(3.2), lg2: calc(3.5) }} sx={{
            display: { xs: 'none', lg: 'block' }
        }} > <SwiperItemSkeleton /> </Grid>
    </Grid>
)

const countDownRenderer = ({ days, hours, minutes, seconds, completed }) => {
    if (completed) {
        // Render a completed state
        return 'تمام شد';
    } else {
        return (
            <Box sx={{
                width: 1,
                maxWidth: 425,
                px: 4,
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                '& > span': {
                    color: '#fff',
                    fontSize: 22
                },
                '& div': {
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    py: 1,
                    bgcolor: uiColors.primary,
                    minWidth: 54,
                    borderRadius: 1,

                    '& span:first-of-type': {
                        color: '#fff',
                        fontWeight: 500,
                        fontSize: 22
                    },
                    '& span:last-of-type': {
                        color: colors.grey[50],
                        fontWeight: 300,
                        fontSize: 14
                    }
                }
            }}>
                <Box>
                    <Typography component="span">{seconds}</Typography>
                    <Typography component="span">ثانیه</Typography>
                </Box>
                <Typography component="span">:</Typography>
                <Box>
                    <Typography component="span">{minutes}</Typography>
                    <Typography component="span">دقیقه</Typography>
                </Box>
                <Typography component="span">:</Typography>
                <Box>
                    <Typography component="span">{hours + (days * 24)}</Typography>
                    <Typography component="span">ساعت</Typography>
                </Box>


            </Box>
        )
    }
};

const SwiperItemSkeleton = ({
    first, last
}: {
    first?: boolean,
    last?: boolean
}) => (
    <Box sx={{
        // aspectRatio: withoutRatio ? undefined : "34 / 47",
        position: 'relative',
        height: 423,
        width: '100%'
    }}>
        <Box sx={{
            borderRadius: first ? "8px 0 0 8px" : null,
            overflow: 'hidden',
            height: '100%',
            width: '100%',
            color: 'inherit',
            backgroundColor: '#ffffff',
        }}>
            {!last && (
                <Fragment>
                    <Box
                        sx={{
                            position: 'relative',
                            aspectRatio: '1 / 1',
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center'
                        }}>
                        <Skeleton variant="rectangular"
                            animation="pulse"
                            sx={{
                                position: 'absolute', inset: 0, height: '100%', zIndex: 2, bgcolor: '#ffffff99',
                            }} />
                        <Image
                            height={1}
                            width={244}
                            src={"/assets/placeholder-product.jpg"}
                            alt="placeholder"
                        // objectFit="contain"
                        />
                    </Box>
                    <Box sx={{ p: 2, pt: 0 }}>
                        <Skeleton animation="wave" variant="text" sx={{ width: '80%', height: 25 }} />
                        <Skeleton animation="wave" variant="text" sx={{ height: 19, mt: 1 }} />
                        <Skeleton animation="wave" variant="text" sx={{ width: '70%', height: 28, mt: 1 }} />
                    </Box>
                </Fragment>
            )}
        </Box>
    </Box >
)
